import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { ArrowDownRight, ArrowUpRight, Check, ChevronDown, Copy, ExternalLink, X } from 'lucide-react';
import { toast } from 'sonner';
import { ErrorState, LoadingState, PageHeader, SeverityBadge } from '@/components/primitives';
import WorldMap from '@/components/map/WorldMap';
import { useTimeRange } from '@/context/TimeRangeContext';
import { countryName, fmtCount } from '@/lib/geo';
import useResource from '@/pages/dashboard/useResource';
import { apiGet, copyText } from '@/pages/dashboard/api';
import '@/styles/threatmap.css';

/*
 * Threat map: three layers over one world map, each with a ranked list that is also the
 * map's text alternative. Layer, country, sector and group live in the URL so a view can be shared.
 */

const LAYERS = [
  { id: 'ransomware', label: 'Ransomware activity', short: 'Ransomware' },
  { id: 'threats', label: 'Your threats', short: 'Your threats' },
  { id: 'iocs', label: 'Indicator infrastructure', short: 'Infrastructure' },
];
const LIST_STEP = 25;

const defang = (ip) => (ip.includes(':') ? ip.replace(/:/g, '[:]') : ip.replace(/\./g, '[.]'));
const pctChange = (now, before) => (before ? Math.round((100 * (now - before)) / before) : null);
const periodLabel = (label) => label.toLowerCase();

function Change({ value, comparable = true }) {
  if (!comparable || value == null) return null;
  if (value === 0) return <span className="tm-chg">no change</span>;
  const Up = value > 0 ? ArrowUpRight : ArrowDownRight;
  return <span className="tm-chg"><Up aria-hidden="true" />{value > 0 ? '+' : '−'}{fmtCount(Math.abs(value))}</span>;
}

function Seg({ value, options, onChange, label }) {
  return (
    <div className="is-seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.id} type="button" aria-pressed={value === o.id} onClick={() => onChange(o.id)} data-testid={`layer-${o.id}`} aria-label={o.label}>
          <span className="tm-l-long">{o.label}</span><span className="tm-l-short" aria-hidden="true">{o.short}</span>
        </button>
      ))}
    </div>
  );
}

function SectorMenu({ value, mySector, sectors, onChange }) {
  const label = value === 'mine' ? `Your sector${mySector ? ` (${mySector})` : ''}` : value === 'all' ? 'All sectors' : value;
  return (
    <Dropdown.Root>
      <Dropdown.Trigger asChild>
        <button type="button" className="sh-ctl tm-ctl" aria-label={`Sector: ${label}`} data-testid="sector-menu">
          <span className="sh-ctl-text">{label}</span>
          <ChevronDown className="sh-ic sh-ic--sm" aria-hidden="true" />
        </button>
      </Dropdown.Trigger>
      <Dropdown.Portal>
        <Dropdown.Content align="start" sideOffset={6} className="sh-pop tm-pop">
          <Dropdown.RadioGroup value={value} onValueChange={onChange}>
            {mySector && (
              <Dropdown.RadioItem value="mine" className="sh-pop-item">
                Your sector ({mySector})<Dropdown.ItemIndicator className="sh-pop-check"><Check className="sh-ic" aria-hidden="true" /></Dropdown.ItemIndicator>
              </Dropdown.RadioItem>
            )}
            <Dropdown.RadioItem value="all" className="sh-pop-item">
              All sectors<Dropdown.ItemIndicator className="sh-pop-check"><Check className="sh-ic" aria-hidden="true" /></Dropdown.ItemIndicator>
            </Dropdown.RadioItem>
            <Dropdown.Separator className="sh-pop-sep" />
            {sectors.map((s) => (
              <Dropdown.RadioItem key={s.sector} value={s.sector} className="sh-pop-item">
                <span className="tm-pop-name">{s.sector}</span><span className="tm-pop-n">{fmtCount(s.count)}</span>
                <Dropdown.ItemIndicator className="sh-pop-check"><Check className="sh-ic" aria-hidden="true" /></Dropdown.ItemIndicator>
              </Dropdown.RadioItem>
            ))}
          </Dropdown.RadioGroup>
        </Dropdown.Content>
      </Dropdown.Portal>
    </Dropdown.Root>
  );
}

function RankList({ rows, max, fpSet, selected, hovered, onSelect, onHover, comparable, unit }) {
  const [shown, setShown] = useState(LIST_STEP);
  useEffect(() => setShown(LIST_STEP), [rows]);
  if (!rows.length) return <p className="tm-empty">No country has activity in this period.</p>;
  return (
    <>
      <ol className="tm-rank" aria-label={`Countries by ${unit}`}>
        {rows.slice(0, shown).map((r) => (
          <li key={r.iso2}>
            <button type="button" className={`tm-row${hovered === r.iso2 ? ' is-hl' : ''}${selected === r.iso2 ? ' is-on' : ''}`}
              onClick={() => onSelect(r.iso2)} onMouseEnter={() => onHover(r.iso2)} onMouseLeave={() => onHover(null)}
              onFocus={() => onHover(r.iso2)} onBlur={() => onHover(null)} data-testid={`rank-${r.iso2}`}>
              <span className="tm-row-name">
                <span className="tm-iso">{r.iso2}</span>
                <span className="tm-row-label">{r.name}</span>
                {fpSet.has(r.iso2) && <span className="tm-fp-tag">Footprint</span>}
              </span>
              <span className="tm-row-fig">
                <b>{fmtCount(r.count)}</b>
                <Change value={r.change} comparable={comparable} />
              </span>
              <span className="tm-row-bar" aria-hidden="true"><i style={{ width: `${Math.max(2, (100 * r.count) / (max || 1))}%` }} /></span>
            </button>
          </li>
        ))}
      </ol>
      {rows.length > shown && (
        <button type="button" className="is-btn is-btn--ghost is-btn--sm tm-more" onClick={() => setShown((n) => n + LIST_STEP * 4)}>
          Show {Math.min(rows.length - shown, LIST_STEP * 4)} more of {rows.length}
        </button>
      )}
    </>
  );
}

function Trend({ series, step }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  return (
    <figure className="tm-trend">
      <div className="tm-trend-bars" role="img" aria-label={`Victims per ${step}: ${series.map((s) => s.count).join(', ')}`}>
        {series.map((s) => (
          <span key={s.start} title={`${step === 'week' ? 'Week of' : 'Month from'} ${s.start}: ${s.count}`}>
            <i style={{ height: `${s.count ? Math.max(6, (100 * s.count) / max) : 0}%` }} />
          </span>
        ))}
      </div>
      <figcaption>Per {step}, from {series[0]?.start}</figcaption>
    </figure>
  );
}

function PanelHead({ iso2, inFootprint, onClose }) {
  return (
    <div className="tm-panel-h">
      <div className="min-w-0">
        <h2 className="tm-panel-title">{countryName(iso2)}</h2>
        {inFootprint && <span className="tm-fp-tag">In your footprint</span>}
      </div>
      <button type="button" className="tm-x" onClick={onClose} aria-label="Close country detail"><X aria-hidden="true" /></button>
    </div>
  );
}

function RansomwareCountry({ iso2, row, days, sector, group, mySector, onGroup, threatsHere, iocsHere, onLayer, rangeLabel, comparable }) {
  const detail = useResource(() => apiGet(`/geo/ransomware/${iso2}`, { days, sector, group: group || undefined }), [iso2, days, sector, group], { poll: false });
  const d = detail.data;
  return (
    <div className="tm-panel-b">
      <div className="tm-facts">
        <div><span>Victims, {periodLabel(rangeLabel)}</span><b>{fmtCount(row?.count ?? d?.count ?? 0)}</b></div>
        <div><span>Period before</span><b>{comparable ? fmtCount(row?.previous ?? 0) : '-'}</b></div>
      </div>
      {detail.loading && !d ? <LoadingState label="country detail" rows={3} />
        : detail.error && !d ? <ErrorState title="Couldn't load this country" error={detail.error} onRetry={detail.reload} />
          : d && (
            <>
              <section>
                <h3 className="tm-h3">Trend</h3>
                <Trend series={d.series} step={d.series_step} />
              </section>
              <section>
                <h3 className="tm-h3">Most active groups</h3>
                {d.top_groups.length ? (
                  <ul className="tm-kv">
                    {d.top_groups.map((g) => (
                      <li key={g.group}>
                        <button type="button" className="tm-link" onClick={() => onGroup(g.group)} title={`Show only ${g.group} on the map`}>{g.group}</button>
                        <b>{fmtCount(g.count)}</b>
                      </li>
                    ))}
                  </ul>
                ) : <p className="tm-note">No listings in this period.</p>}
              </section>
              {!sector && d.sectors.length > 0 && (
                <section>
                  <h3 className="tm-h3">Sectors</h3>
                  <ul className="tm-kv">
                    {d.sectors.slice(0, 6).map((s) => (
                      <li key={s.sector} className={s.sector === mySector ? 'is-mine' : undefined}>
                        <span>{s.sector}{s.sector === mySector ? ' (yours)' : ''}</span><b>{fmtCount(s.count)}</b>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </>
          )}
      <section className="tm-cross">
        <h3 className="tm-h3">Also here</h3>
        <button type="button" className="tm-cross-row" onClick={() => onLayer('threats')}>
          <span>Your threats naming {countryName(iso2)}</span><b>{fmtCount(threatsHere)}</b>
        </button>
        <button type="button" className="tm-cross-row" onClick={() => onLayer('iocs')}>
          <span>Indicator IPs located here</span><b>{fmtCount(iocsHere)}</b>
        </button>
      </section>
    </div>
  );
}

function ThreatsCountry({ entry }) {
  if (!entry) return <div className="tm-panel-b"><p className="tm-note">None of your threats in this period name this country.</p></div>;
  return (
    <div className="tm-panel-b">
      <div className="tm-facts">
        <div><span>Threats naming it</span><b>{fmtCount(entry.count)}</b></div>
        <div><span>Critical or High</span><b>{fmtCount((entry.severity.Critical || 0) + (entry.severity.High || 0))}</b></div>
      </div>
      <ul className="tm-threats">
        {entry.threats.map((t) => (
          <li key={t.id}>
            <SeverityBadge severity={t.severity} size="sm" short />
            <Link to={`/threats?id=${encodeURIComponent(t.id)}`} className="tm-threat-link">{t.name}</Link>
          </li>
        ))}
      </ul>
      {entry.count > entry.threats.length && <p className="tm-note">Showing the {entry.threats.length} most recent.</p>}
    </div>
  );
}

function IocsCountry({ indicators }) {
  const copy = async (ip) => { if (await copyText(ip)) toast.success(`Copied ${defang(ip)}`); };
  if (!indicators.length) return <div className="tm-panel-b"><p className="tm-note">No indicator IPs located here in this period.</p></div>;
  return (
    <div className="tm-panel-b">
      <ul className="tm-iocs">
        {indicators.map((i) => (
          <li key={i.ip}>
            <div className="tm-ioc-top">
              <code className="tm-ip">{defang(i.ip)}</code>
              <button type="button" className="tm-x" onClick={() => copy(i.ip)} aria-label={`Copy ${defang(i.ip)}`}><Copy aria-hidden="true" /></button>
            </div>
            {i.sources.map((s) => (
              s.kind === 'threat'
                ? <Link key={s.id} to={`/threats?id=${encodeURIComponent(s.id)}`} className="tm-ioc-src">{s.name}</Link>
                : <Link key={s.id} to="/hunting" className="tm-ioc-src">Hunt: {String(s.name || '').replace(/^TH:\s*/, '')}</Link>
            ))}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function ThreatMap() {
  const { days, range } = useTimeRange();
  const [params, setParams] = useSearchParams();
  const layer = LAYERS.some((l) => l.id === params.get('layer')) ? params.get('layer') : 'ransomware';
  const selected = params.get('country')?.toUpperCase() || null;
  const sectorParam = params.get('sector') || 'mine';
  const group = params.get('group') || null;
  const showFp = params.get('fp') !== '0';
  const [hovered, setHovered] = useState(null);

  const set = useCallback((patch) => {
    setParams((p) => {
      const next = new URLSearchParams(p);
      Object.entries(patch).forEach(([k, v]) => (v == null || v === '' ? next.delete(k) : next.set(k, v)));
      return next;
    }, { replace: true });
  }, [setParams]);

  const fp = useResource(() => apiGet('/geo/footprint'), [], { poll: false });
  const mySector = fp.data?.sector || null;
  const sector = sectorParam === 'mine' ? (mySector ? 'mine' : 'all') : sectorParam;
  const rw = useResource(() => apiGet('/geo/ransomware', { days, sector, group: group || undefined }), [days, sector, group]);
  const th = useResource(() => apiGet('/geo/threats', { since_days: days }), [days]);
  const io = useResource(() => apiGet('/geo/iocs', { since_days: days }), [days]);

  const fpList = useMemo(() => {
    const f = fp.data;
    if (!f) return [];
    return f.basis === 'countries' ? f.countries : f.region_countries;
  }, [fp.data]);
  const fpSet = useMemo(() => new Set(fpList), [fpList]);

  const active = layer === 'ransomware' ? rw : layer === 'threats' ? th : io;
  // A country whose count fell to zero keeps its "previous" figure in the API but is not ranked as active.
  const rows = useMemo(() => (active.data?.countries || []).filter((r) => r.count > 0), [active.data]);
  const values = useMemo(() => Object.fromEntries(rows.map((r) => [r.iso2, r.count])), [rows]);
  const byIso = useMemo(() => Object.fromEntries((active.data?.countries || []).map((r) => [r.iso2, r])), [active.data]);
  const thByIso = useMemo(() => Object.fromEntries((th.data?.countries || []).map((r) => [r.iso2, r])), [th.data]);
  const ioByIso = useMemo(() => {
    const out = {};
    (io.data?.indicators || []).forEach((i) => { if (i.iso2) (out[i.iso2] = out[i.iso2] || []).push(i); });
    return out;
  }, [io.data]);
  const max = rows[0]?.count || 0;
  const comparable = layer === 'ransomware' && !!rw.data?.comparable;
  const rwSectorName = rw.data?.sector;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && selected && !e.defaultPrevented) set({ country: null }); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected, set]);

  const fpTotal = useMemo(() => rows.filter((r) => fpSet.has(r.iso2)).reduce((a, r) => a + r.count, 0), [rows, fpSet]);

  const summary = (() => {
    const d = active.data;
    if (!d) return null;
    const when = `in the ${periodLabel(range.label)}`;
    if (layer === 'ransomware') {
      const delta = d.comparable ? pctChange(d.total, d.previous_total) : null;
      return (
        <>
          <b>{fmtCount(d.total)}</b> organizations{rwSectorName ? <> in {rwSectorName}</> : null}{group ? <> claimed by {group}</> : null} were listed on ransomware leak sites {when}
          {delta != null ? <>, {delta >= 0 ? 'up' : 'down'} {Math.abs(delta)}% on the period before</> : null}.
          {d.unknown_country > 0 && <> {fmtCount(d.unknown_country)} have no country and are not on the map.</>}
        </>
      );
    }
    if (layer === 'threats') {
      const placed = d.total - d.worldwide - d.unplaced - d.regions.reduce((a, r) => a + r.count, 0);
      return (
        <>
          Of <b>{fmtCount(d.total)}</b> threats matched to you {when}, {fmtCount(Math.max(0, placed))} name a specific country.
          {' '}The rest name only a region or are worldwide, and are listed beside the map rather than spread across countries.
        </>
      );
    }
    return (
      <>
        <b>{fmtCount(d.total)}</b> public IP {d.total === 1 ? 'address' : 'addresses'} from your threats and published hunt campaigns {when}, located in {fmtCount(d.countries.length)} {d.countries.length === 1 ? 'country' : 'countries'}.
        {' '}Domains are never resolved, so the map does not contact the infrastructure it shows.
      </>
    );
  })();

  const renderTip = (iso2, fallbackName) => {
    const r = byIso[iso2];
    const name = countryName(iso2) || fallbackName;
    return (
      <>
        <b>{name}</b>
        {layer === 'ransomware' && (r ? (
          <>
            <span className="wm-tip-row"><span>Victims</span><span>{fmtCount(r.count)}</span></span>
            {comparable && <span className="wm-tip-row"><span>Period before</span><span>{fmtCount(r.previous)}</span></span>}
          </>
        ) : <span>No listings in this period</span>)}
        {layer === 'threats' && <span className="wm-tip-row"><span>Your threats naming it</span><span>{fmtCount(r?.count || 0)}</span></span>}
        {layer === 'iocs' && <span className="wm-tip-row"><span>Indicator IPs</span><span>{fmtCount(r?.count || 0)}</span></span>}
        {fpSet.has(iso2) && <span className="wm-tip-hint">In your footprint</span>}
        {r && <span className="wm-tip-hint">Click for detail</span>}
      </>
    );
  };

  const fpLabel = fp.data?.basis === 'countries' ? 'your countries' : fp.data?.region ? `your region (${fp.data.region})` : null;

  return (
    <div className="pg tm" data-testid="threat-map-page">
      <PageHeader
        title="Threat map"
        subtitle="Where ransomware is landing, where the threats matched to you are aimed, and where their infrastructure sits."
        actions={<Seg value={layer} options={LAYERS} onChange={(id) => set({ layer: id === 'ransomware' ? null : id })} label="Map layer" />}
      />

      <div className="tm-toolbar">
        {layer === 'ransomware' && (
          <>
            <SectorMenu value={sector} mySector={mySector} sectors={rw.data?.sectors || []} onChange={(v) => set({ sector: v === 'mine' ? null : v })} />
            {group && (
              <button type="button" className="tm-filter" onClick={() => set({ group: null })} aria-label={`Remove group filter ${group}`}>
                Group: {group} <X aria-hidden="true" />
              </button>
            )}
          </>
        )}
        {fpLabel && (
          <label className="tm-check">
            <input type="checkbox" checked={showFp} onChange={(e) => set({ fp: e.target.checked ? null : '0' })} />
            Outline {fpLabel}
          </label>
        )}
        <span className="tm-spacer" />
        <span className="tm-hint">Ctrl + scroll to zoom, drag to pan</span>
      </div>

      {summary && <p className="tm-summary" data-testid="map-summary">{summary}</p>}

      <div className="tm-layout">
        <section className="tm-mapcard" aria-label="Map">
          {active.error && !active.data ? <ErrorState title="Couldn't load this layer" error={active.error} onRetry={active.reload} />
            : (
              <WorldMap
                values={values}
                footprint={showFp ? fpList : []}
                selected={selected}
                highlight={hovered}
                onHover={setHovered}
                onSelect={(iso2) => set({ country: selected === iso2 ? null : iso2 })}
                renderTip={renderTip}
                label={`World map of ${LAYERS.find((l) => l.id === layer).label.toLowerCase()}. The country list beside it has the same figures.`}
                legendLabel={layer === 'ransomware' ? 'Victims per country' : layer === 'threats' ? 'Threats naming the country' : 'Indicator IPs per country'}
                height="auto"
              />
            )}
          <footer className="tm-source">
            {layer === 'ransomware' && <>Source: <a href="https://www.ransomware.live" target="_blank" rel="noopener noreferrer">ransomware.live <ExternalLink aria-hidden="true" /></a>. A listing is the group&apos;s claim, not a confirmed breach. Counts only; victim names are not shown.</>}
            {layer === 'threats' && <>Countries come from the analysis of each threat: where it names targets or victims, not where the attacker is.</>}
            {layer === 'iocs' && <><a href="https://db-ip.com" target="_blank" rel="noopener noreferrer">IP geolocation by DB-IP <ExternalLink aria-hidden="true" /></a> (CC BY 4.0){io.data?.database?.built ? `, database of ${io.data.database.built}` : ''}. Located offline.</>}
          </footer>
        </section>

        <aside className="tm-panel" aria-label={selected ? 'Country detail' : 'Countries'}>
          <div className="tm-panel-in">
            {selected ? (
              <>
                <PanelHead iso2={selected} inFootprint={fpSet.has(selected)} onClose={() => set({ country: null })} />
                {layer === 'ransomware' && (
                  <RansomwareCountry iso2={selected} row={byIso[selected]} days={days} sector={sector === 'all' ? undefined : sector} group={group}
                    mySector={mySector} onGroup={(g) => set({ group: g })} threatsHere={thByIso[selected]?.count || 0}
                    iocsHere={(ioByIso[selected] || []).length} onLayer={(id) => set({ layer: id })} rangeLabel={range.label} comparable={comparable} />
                )}
                {layer === 'threats' && <ThreatsCountry entry={thByIso[selected]} />}
                {layer === 'iocs' && <IocsCountry indicators={ioByIso[selected] || []} />}
              </>
            ) : (
              <>
                <div className="tm-panel-h">
                  <h2 className="tm-panel-title">Countries</h2>
                  <span className="tm-panel-meta">{rows.length ? `${rows.length} with activity` : ''}</span>
                </div>
                {fpList.length > 0 && active.data && (
                  <div className="tm-fp-sum">
                    <span>In {fpLabel}</span>
                    <b>{fmtCount(fpTotal)}</b>
                  </div>
                )}
                <div className="tm-panel-b tm-panel-b--list">
                  {active.loading && !active.data ? <LoadingState label="countries" rows={8} />
                    : (
                      <RankList rows={rows} max={max} fpSet={fpSet} selected={selected} hovered={hovered}
                        onSelect={(iso2) => set({ country: iso2 })} onHover={setHovered} comparable={comparable}
                        unit={layer === 'ransomware' ? 'victims' : layer === 'threats' ? 'threats' : 'indicators'} />
                    )}
                  {layer === 'threats' && th.data && (th.data.regions.length > 0 || th.data.worldwide > 0) && (
                    <div className="tm-regions">
                      <h3 className="tm-h3">Region only</h3>
                      <ul className="tm-kv">
                        {th.data.regions.map((r) => <li key={r.region}><span>{r.region}</span><b>{fmtCount(r.count)}</b></li>)}
                        {th.data.worldwide > 0 && <li><span>Worldwide</span><b>{fmtCount(th.data.worldwide)}</b></li>}
                      </ul>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}
